import { Injectable } from '@angular/core';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsOption } from 'echarts';

@Injectable({ providedIn: 'root' })
export class EChartsConfigService {

  static readonly COLORS = [
    '#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9',
    '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'
  ];

  static readonly PRIMARY_AXIS_COLOR = 'rgb(0,124,190)';
  static readonly SECONDARY_AXIS_COLOR = 'rgb(249,163,70)';

  constructor(private translateSrv: TranslateService) {}

  getColor(index: number): string {
    return EChartsConfigService.COLORS[index % EChartsConfigService.COLORS.length];
  }

  getDefaultChartOptions(locale: string): EChartsOption {
    const decimalPoint = getLocaleNumberSymbol(locale, NumberSymbol.Decimal);
    const thousandsSep = getLocaleNumberSymbol(locale, NumberSymbol.Group);

    return {
      grid: {
        left: 70,
        right: 70,
        top: 45,
        bottom: 50,
        containLabel: true
      },
      legend: {
        type: 'scroll',
        bottom: 10,
        left: 'center',
        orient: 'horizontal',
        textStyle: { fontSize: 12 }
      },
      xAxis: {
        type: 'category',
        data: [],
        show: true,
        triggerEvent: true,
        axisLabel: {
          show: true,
          color: '#0094D1',
          fontSize: 12,
          interval: 0,
          hideOverlap: true
        },
        axisLine: { show: true },
        axisTick: { show: true }
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: {
            color: EChartsConfigService.PRIMARY_AXIS_COLOR,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          splitLine: { show: true }
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            color: EChartsConfigService.SECONDARY_AXIS_COLOR,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          splitLine: { show: false }
        }
      ],
      tooltip: {
        trigger: 'item',
        enterable: false,
        transitionDuration: 0,
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          if (!items.length) return '';
          const date = items[0].name || '';
          const lines = items.map(p =>
            `${p.marker} ${p.seriesName} &nbsp;&nbsp;<b>${p.value}</b>`
          );
          return `${date}<br>${lines.join('<br>')}`;
        },
        position: (point: number[], params: any, dom: any, rect: any, size: any) => {
          const [tipWidth, tipHeight] = size.contentSize;
          const [viewWidth, viewHeight] = size.viewSize;
          const gap = 10;
          let x = point[0] - tipWidth / 2;
          let y = point[1] - tipHeight - gap;
          // If not enough space above, position below the point
          if (y < 0) {
            y = point[1] + gap;
          }
          // Clamp horizontally
          if (x < 0) x = 0;
          if (x + tipWidth > viewWidth) x = viewWidth - tipWidth;
          return [x, y];
        }
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, filterMode: 'none', zoomOnMouseWheel: true, moveOnMouseMove: false, moveOnMouseWheel: false }
      ],
      series: [],
      animation: true
    };
  }

  formatNumber(value: number, locale: string, decimalPoint: string, thousandsSep: string): string {
    if (value == null || isNaN(value)) return '';
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return parts.join(decimalPoint);
  }

  buildSeries(options: {
    name: string;
    id: string;
    data: any[];
    isPrimary: boolean;
    isDraggable: boolean;
    color: string;
    decimal?: number;
    metadata?: any;
  }): any {
    return {
      name: options.name,
      id: options.id,
      type: 'line',
      yAxisIndex: options.isPrimary ? 0 : 1,
      lineStyle: {
        type: options.isPrimary ? 'solid' : 'dashed',
        color: options.color
      },
      itemStyle: { color: options.color },
      symbol: 'circle',
      symbolSize: 8,
      cursor: options.isDraggable ? 'pointer' : 'default',
      data: options.data,
      _metadata: options.metadata,
      _isDraggable: options.isDraggable,
      _decimal: options.decimal
    };
  }
}
